import {getConfig} from '../config';

type ExtractPathParams<T extends string> =
  T extends `${string}:${infer Param}/${infer Rest}`
    ? Param | ExtractPathParams<`/${Rest}`>
    : T extends `${string}:${infer Param}`
      ? Param
      : never;

const buildPath = <T extends string>(
  template: T,
  params: Record<ExtractPathParams<T>, string> | undefined,
): string => {
  if (params === undefined) return template;
  return template.replace(
    /:([a-zA-Z0-9_]+)/g,
    (_, key: ExtractPathParams<T>) => {
      const value = params[key];
      if (!value) throw new Error(`Missing value for path param: ${key}`);
      return encodeURIComponent(value);
    },
  );
};

function buildQuery(
  query?: Record<string, string | number | boolean | undefined | null>,
): string {
  if (!query) return '';
  const queryString = Object.entries(query)
    .filter(([, value]) => value !== undefined && value !== null)
    .map(([key, value]) => {
      return `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`;
    })
    .join('&');
  return queryString ? `?${queryString}` : '';
}

export type QueryType = Record<
  string,
  string | number | boolean | undefined | null
>;

type BuildFunction<TPath extends string, TQuery extends QueryType> =
  ExtractPathParams<TPath> extends never
    ? (options?: {query?: TQuery}) => string
    : (options: {
        params: Record<ExtractPathParams<TPath>, string>;
        query?: TQuery;
      }) => string;

const hosts: Record<string, string> = {
  production: 'https://apigw.resurs.com',
  integration: 'https://apigw.integration.resurs.com',
  development: 'https://apigw.integration.resurs.com',
  local: 'https://apigw.integration.resurs.com',
};

const getHost = () => {
  const env = getConfig()?.env ?? 'production';
  return hosts[env] ?? hosts['production'];
};

export const createEndpoint = <TPath extends string, TQuery extends QueryType>(
  path: TPath,
) => {
  type Params = Record<ExtractPathParams<TPath>, string>;

  const build = ((options?: {params?: Params; query?: TQuery}) => {
    return `${getHost()}${buildPath(path, options?.params)}${buildQuery(
      options?.query,
    )}`;
  }) as BuildFunction<TPath, TQuery>;

  return {
    template: `*${path}`,
    build,
  };
};
